import React from "react";
import Api from "@/api";
import csrftoken from "@/utils/csrf_token";

// Right-rail blocker board: what each team is waiting on, one line per team, refreshing every few
// seconds. Fed by the agents' SIGN-OFF (report_blocker/clear_blocker). "Waiting on YOU" is the
// owner's action queue, so it sits on top — and each of those rows carries a RESOLVE button: the
// owner types the answer, and one click files it as a task to the agent, clears the blocker, and
// fires an immediate wake that hands the agent the resolution in its prompt. A team not listed is
// unblocked. Always renders a visible frame (loading / all-clear / list) so the rail is never
// mysteriously empty.

interface Blocker {
  id: string;
  team: string;
  summary: string;
  waiting_on: "owner" | "team" | "external";
  target_team?: string | null;
  raised_by?: string | null;
  reaffirmed_at?: string | null;
  inserted_at?: string | null;
  status?: "active" | "resolved";
  resolution?: string | null;
  resolved_at?: string | null;
  resolved_by?: string | null;
}

const STALE_MS = 6 * 60 * 60 * 1000; // no reaffirm in 6h => a live agent isn't touching it => stale

function ago(iso?: string | null): string {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins >= 1440) return `${Math.floor(mins / 1440)}d`;
  if (mins >= 60) return `${Math.floor(mins / 60)}h`;
  return `${Math.max(1, mins)}m`;
}

export function BlockersPanel({ companyId }: { companyId: string }) {
  const [blockers, setBlockers] = React.useState<Blocker[] | null>(null);
  const [errored, setErrored] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const res = await Api.blockers.list({ companyId });
      setBlockers(JSON.parse(res.blockers || "[]"));
      setErrored(false);
    } catch {
      setErrored(true);
    }
  }, [companyId]);

  React.useEffect(() => {
    load();
    const timer = setInterval(load, 5000); // "every couple seconds", cheap query
    return () => clearInterval(timer);
  }, [load]);

  const list = blockers || [];
  const active = list.filter((b) => (b.status || "active") === "active");
  const resolved = list.filter((b) => b.status === "resolved");
  const owner = active.filter((b) => b.waiting_on === "owner");
  const team = active.filter((b) => b.waiting_on === "team");
  const external = active.filter((b) => b.waiting_on === "external");

  return (
    <div className="rounded-lg border border-surface-outline bg-surface-base overflow-hidden" data-test-id="blockers-panel">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-surface-outline">
        <div className="font-semibold text-content-accent text-sm">What the fleet is waiting on</div>
        <span className="text-xs text-content-dimmed">
          {blockers === null ? "…" : active.length === 0 ? "🟢 all clear" : `${active.length} blocked`}
        </span>
      </div>

      {blockers === null ? (
        <div className="px-4 py-3 text-sm text-content-dimmed">{errored ? "Couldn't load blockers." : "Loading…"}</div>
      ) : list.length === 0 ? (
        <div className="px-4 py-3 text-sm text-content-dimmed">Every team is unblocked and running.</div>
      ) : (
        <div className="divide-y divide-surface-outline">
          {active.length === 0 ? (
            <div className="px-4 py-3 text-sm text-content-dimmed">Every team is unblocked and running.</div>
          ) : null}
          <Group title="⚠️ Waiting on you" rows={owner} highlight resolvable companyId={companyId} onResolved={load} />
          <Group title="Waiting on another team" rows={team} />
          <Group title="Waiting on something external" rows={external} />
          <ResolvedGroup rows={resolved} />
        </div>
      )}
    </div>
  );
}

// The 72h ledger: what got resolved and HOW. Rulings the owner typed are shown verbatim — the same
// text every agent now sees in its wake prompt, so the owner can trust the fleet is reading from
// the same page he is. Rows with no resolution text were cleared by the team itself.
function ResolvedGroup({ rows }: { rows: Blocker[] }) {
  if (rows.length === 0) return null;

  return (
    <div className="px-4 py-2.5 bg-surface-dimmed/40">
      <div className="text-xs font-semibold uppercase tracking-wide text-content-dimmed mb-1.5">
        ✅ Resolved (last 72h)
      </div>
      <div className="space-y-2.5">
        {rows.map((b) => (
          <div key={b.id} className="text-sm opacity-80">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-content-accent truncate">{b.team}</span>
              <span className="text-xs text-content-dimmed shrink-0 whitespace-nowrap">{ago(b.resolved_at)} ago</span>
            </div>
            <div className="text-content-dimmed leading-snug line-through decoration-content-subtle">{b.summary}</div>
            {b.resolution ? (
              <div className="text-content-base leading-snug mt-0.5">
                <span className="text-xs font-semibold text-content-dimmed">{b.resolved_by || "Owner"}: </span>
                {b.resolution}
              </div>
            ) : (
              <div className="text-xs text-content-dimmed mt-0.5">cleared by the team</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Group({
  title,
  rows,
  highlight,
  resolvable,
  companyId,
  onResolved,
}: {
  title: string;
  rows: Blocker[];
  highlight?: boolean;
  resolvable?: boolean;
  companyId?: string;
  onResolved?: () => void;
}) {
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);

  if (rows.length === 0) return null;

  const submit = async (b: Blocker) => {
    if (!companyId || sending) return;
    setSending(true);
    try {
      const res = await fetch(`/${companyId}/api/blockers/${b.id}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-csrf-token": csrftoken() || "",
        },
        body: JSON.stringify({ message }),
      });
      if (res.ok) {
        setOpenId(null);
        setMessage("");
        onResolved && onResolved();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={"px-4 py-2.5" + (highlight ? " border-l-2 border-content-accent" : "")}>
      <div className="text-xs font-semibold uppercase tracking-wide text-content-dimmed mb-1.5">{title}</div>
      <div className="space-y-2.5">
        {rows.map((b) => {
          const stale = b.reaffirmed_at ? Date.now() - new Date(b.reaffirmed_at).getTime() > STALE_MS : false;
          const open = openId === b.id;
          return (
            <div key={b.id} className={"text-sm" + (stale ? " opacity-50" : "")}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-content-accent truncate">{b.team}</span>
                <span className="text-xs text-content-dimmed shrink-0 whitespace-nowrap">
                  {ago(b.inserted_at)}
                  {stale ? " · stale" : ""}
                </span>
              </div>
              <div className="text-content-base leading-snug">{b.summary}</div>
              {b.target_team ? <div className="text-xs text-content-dimmed mt-0.5">→ {b.target_team}</div> : null}

              {resolvable ? (
                open ? (
                  <div className="mt-1.5 space-y-1.5" data-test-id="blocker-resolve-form">
                    <textarea
                      className="w-full text-sm rounded border border-surface-outline bg-surface-base px-2 py-1.5"
                      rows={3}
                      autoFocus
                      placeholder="Your answer / decision — this goes straight to the agent and wakes it immediately"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        className="text-xs font-semibold rounded px-2.5 py-1 bg-accent-1 text-white-1 disabled:opacity-50"
                        disabled={sending}
                        onClick={() => submit(b)}
                      >
                        {sending ? "Sending…" : "Resolve & wake the team"}
                      </button>
                      <button
                        className="text-xs text-content-dimmed underline"
                        onClick={() => {
                          setOpenId(null);
                          setMessage("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="mt-1 text-xs font-semibold text-content-accent underline"
                    data-test-id="blocker-resolve-button"
                    onClick={() => {
                      setOpenId(b.id);
                      setMessage("");
                    }}
                  >
                    Resolve…
                  </button>
                )
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
